import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Home as HomeIcon, History as HistoryIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';
import { Client } from '../../types/client';

interface AddressHistoryModalProps {
  open: boolean;
  onClose: () => void;
  client: Client;
}

const AddressHistoryModal: React.FC<AddressHistoryModalProps> = ({ open, onClose, client }) => {
  // Log client data when modal opens
  React.useEffect(() => {
    if (open) {
      console.log('AddressHistoryModal - Client data:', {
        clientId: client._id,
        homeAddress: client.homeAddress,
        movedInDate: client.homeAddress?.movedInDate,
        movedInDateType: typeof client.homeAddress?.movedInDate,
        movedInDateValue: client.homeAddress?.movedInDate,
      });
    }
  }, [open, client]);

  const formatDate = (date: string | Date | any | undefined) => {
    console.log('formatDate called with:', { date, type: typeof date });
    
    if (!date) {
      console.log('formatDate: No date provided, returning Present');
      return 'Present';
    }
    
    // Handle MongoDB extended JSON format: { "$date": { "$numberLong": "timestamp" } }
    if (typeof date === 'object' && date.$date) {
      const timestamp = date.$date.$numberLong 
        ? parseInt(date.$date.$numberLong) 
        : date.$date;
      const formatted = new Date(timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      console.log('formatDate: MongoDB format converted:', { original: date, timestamp, formatted });
      return formatted;
    }
    
    // Handle regular Date object or ISO string
    const formatted = new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    console.log('formatDate: Standard format converted:', { original: date, formatted });
    return formatted;
  };

  const formatAddress = (address: any) => {
    if (!address) return 'No address provided';
    return [
      address.line1,
      address.line2,
      address.city,
      address.county,
      address.postcode
    ].filter(Boolean).join(', ');
  };

  const validation = client.addressValidation;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e293b',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">Address History</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {/* Validation Status */}
        {validation && (
          <Box 
            mb={3} 
            p={2} 
            sx={{ 
              backgroundColor: validation.hasGaps || validation.hasOverlaps ? 'rgba(237, 108, 2, 0.08)' : 'rgba(46, 125, 50, 0.08)', 
              borderRadius: 2, 
              border: validation.hasGaps || validation.hasOverlaps ? '1px solid rgba(237, 108, 2, 0.3)' : '1px solid rgba(46, 125, 50, 0.3)' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {validation.hasGaps || validation.hasOverlaps ? (
                <WarningIcon sx={{ color: '#ED6C02' }} />
              ) : (
                <CheckCircleIcon sx={{ color: '#2E7D32' }} />
              )}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: validation.hasGaps || validation.hasOverlaps ? '#ED6C02' : '#2E7D32' }}>
                Validation Status
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Coverage:</strong> {Math.floor(validation.totalMonths / 12)} years {validation.totalMonths % 12} months
              {validation.totalMonths >= 60 && ' (5+ years ✓)'}
            </Typography>
            {validation.hasGaps && (
              <Typography variant="body2" color="error">
                ⚠️ Insufficient coverage (need 5 years)
              </Typography>
            )}
            {validation.hasOverlaps && (
              <Typography variant="body2" color="error">
                ⚠️ Overlapping address dates detected
              </Typography>
            )}
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Validated: {formatDate(validation.validatedAt)}
            </Typography>
          </Box>
        )}

        {/* Current Home Address */}
        {client.homeAddress && (
          <Box mb={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HomeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Current Home Address</Typography>
              <Chip label="Current" size="small" color="primary" />
            </Box>
            <Box 
              p={2} 
              sx={{ 
                backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                borderRadius: 2, 
                border: '1px solid rgba(25, 118, 210, 0.2)' 
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {formatAddress(client.homeAddress)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {(() => {
                  const hasMovedInDate = client.homeAddress?.movedInDate;
                  console.log('Rendering moved in date section:', {
                    hasMovedInDate,
                    movedInDate: client.homeAddress?.movedInDate,
                    condition: !!hasMovedInDate
                  });
                  
                  return hasMovedInDate ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Moved In:</strong> {formatDate(client.homeAddress.movedInDate)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>→</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Present</strong>
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Moved in date not recorded
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        )}

        {/* Address History */}
        {client.addressHistory && client.addressHistory.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Previous Addresses
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {client.addressHistory
                .sort((a, b) => new Date(b.movedInDate).getTime() - new Date(a.movedInDate).getTime())
                .map((addr, index) => (
                  <Box 
                    key={index} 
                    p={2} 
                    sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                      borderRadius: 2, 
                      border: '1px solid rgba(0, 0, 0, 0.1)' 
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {formatAddress(addr.address)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Moved In:</strong> {formatDate(addr.movedInDate)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Moved Out:</strong> {formatDate(addr.movedOutDate)}
                      </Typography>
                      {addr.isAbroad && (
                        <Chip label="Abroad" size="small" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        )}

        {/* No Address History */}
        {(!client.addressHistory || client.addressHistory.length === 0) && !client.homeAddress && (
          <Box 
            p={4} 
            sx={{ 
              textAlign: 'center', 
              backgroundColor: 'rgba(0, 0, 0, 0.02)', 
              borderRadius: 2 
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No address history available
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressHistoryModal;
